import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { TenantService } from '../../../core/tenant/tenant.service';
import { OperationsApiService } from '../../../core/api';
import type { ProjectUiModel } from '../../../core/types';
import { mapProjectContractToUiModel } from '../adapters/project.adapter';
import { isCompanyChangedAbort, runWithTenantRetries } from '../../../core/utils/tenant-retries';

@Injectable({ providedIn: 'root' })
export class ProjectsStore {
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly api = inject(OperationsApiService);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly projectsSignal = signal<ProjectUiModel[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly projects = this.projectsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly hasData = computed(() => this.projects().length > 0);

  constructor() {
    effect(() => {
      void this.tenant.activeCompany();
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.projectsSignal.set([]);
        this.loadingSignal.set(false);
        this.errorSignal.set(null);
        return;
      }
      /** Só após GET com sucesso — se falhar, `activeCompany` pode atualizar e o efeito tenta de novo. */
      if (this.loadedCompanyIdSignal() === companyId) return;
      void this.load();
    });
  }

  load(): void {
    void this.loadAsync();
  }

  private async loadAsync(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const projects = await runWithTenantRetries(requestCompanyId, () => this.tenant.companyId(), () =>
        this.api.getProjects(),
      );
      if (this.tenant.companyId() !== requestCompanyId) return;
      this.projectsSignal.set(projects.map(mapProjectContractToUiModel));
      this.loadedCompanyIdSignal.set(requestCompanyId);
    } catch (e) {
      if (this.tenant.companyId() !== requestCompanyId) return;
      if (isCompanyChangedAbort(e)) return;
      this.errorSignal.set(this.i18n.t('projects.store.loadError'));
      this.projectsSignal.set([]);
    } finally {
      if (this.tenant.companyId() === requestCompanyId) {
        this.loadingSignal.set(false);
      }
    }
  }

  getById(projectId: string | null): ProjectUiModel | null {
    if (!projectId) return null;
    return this.projects().find((project) => project.id === projectId) ?? null;
  }

  reset(): void {
    this.loadedCompanyIdSignal.set(null);
    this.projectsSignal.set([]);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
